import React, { useState } from "react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * The HERald newsletter signup.
 *
 * Props:
 *  - variant: "panel" (default — full editorial panel) | "inline" (compact)
 *  - source: analytics tag ("blog-index", "footer", etc.)
 */
export default function HeraldSignup({ variant = "panel", source = "blog" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (!form.consent) {
      setError("Please consent to receive emails and texts.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/newsletter/subscribe", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        consent: true,
        source,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const dark = variant === "footer";
  const fld = dark
    ? "w-full rounded-lg px-4 py-3 text-base font-medium outline-none transition-colors bg-[rgba(234,216,199,0.08)] border border-[rgba(234,216,199,0.2)] text-[var(--lh-warm-ivory)] placeholder:text-[rgba(234,216,199,0.5)] focus:border-[var(--lh-peach)] focus:ring-2 focus:ring-[var(--lh-peach)]/30"
    : "w-full rounded-lg px-4 py-3 text-base font-medium outline-none transition-colors bg-white border border-[var(--lh-border)] text-[var(--lh-espresso-noir)] focus:border-[var(--lh-peach)] focus:ring-2 focus:ring-[var(--lh-peach)]/30";

  if (done) {
    return (
      <div
        className={`rounded-2xl p-7 text-center ${dark ? "" : ""}`}
        style={dark
          ? { background: "rgba(255,168,115,0.12)", border: "1px solid var(--lh-peach)" }
          : { background: "var(--lh-soft-blush)", border: "1px solid var(--lh-peach)" }}
        data-testid="herald-success"
      >
        <CheckCircle2
          size={36}
          className="mx-auto mb-3"
          style={{ color: dark ? "var(--lh-peach)" : "var(--lh-cognac)" }}
          aria-hidden="true"
        />
        <div
          className="font-display-bold text-3xl tracking-tighter leading-[1.0] mb-2"
          style={{ color: dark ? "var(--lh-warm-ivory)" : "var(--lh-espresso-noir)" }}
        >
          You&apos;re <em>in.</em>
        </div>
        <p
          className="text-sm font-medium"
          style={{ color: dark ? "rgba(234,216,199,0.85)" : "var(--lh-espresso-noir)" }}
        >
          Your first HERald lands in your inbox this week.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-7 lg:p-10"
      style={dark
        ? { background: "rgba(0,0,0,0.25)", border: "1px solid rgba(234,216,199,0.18)" }
        : { background: "var(--lh-soft-blush)", border: "1px solid var(--lh-border)" }}
      data-testid="herald-signup"
    >
      <div
        className="overline mb-3"
        style={{ color: dark ? "var(--lh-peach)" : "var(--lh-cognac)" }}
      >
        The HERald · Weekly Letter
      </div>
      <h3
        className="font-display-bold text-3xl md:text-4xl tracking-tighter leading-[1.0] mb-3"
        style={{ color: dark ? "var(--lh-warm-ivory)" : "var(--lh-espresso-noir)" }}
      >
        Join <em>The HERald.</em>
      </h3>
      <p
        className="text-base font-medium leading-relaxed mb-6 max-w-md"
        style={{ color: dark ? "rgba(234,216,199,0.85)" : "var(--lh-espresso-noir)" }}
      >
        Your weekly dose of visibility, growth, and women building big things locally.
      </p>

      <form onSubmit={submit} className="space-y-3" data-testid="herald-form">
        <input
          type="text"
          required
          placeholder="First name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={fld}
          data-testid="herald-name"
        />
        <input
          type="email"
          required
          placeholder="Email *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={fld}
          data-testid="herald-email"
        />
        <input
          type="tel"
          placeholder="Phone (optional, for SMS)"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className={fld}
          data-testid="herald-phone"
        />

        <label
          className="flex items-start gap-3 cursor-pointer text-sm font-medium leading-snug mt-3"
          style={{ color: dark ? "rgba(234,216,199,0.85)" : "var(--lh-espresso-noir)" }}
          data-testid="herald-consent-label"
        >
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
            className="mt-1 w-4 h-4 accent-[var(--lh-peach)]"
            data-testid="herald-consent"
          />
          <span>
            I consent to receive emails and texts from LocalizeHER. I can unsubscribe at any time.
          </span>
        </label>

        {error && (
          <div
            className="text-sm font-semibold pt-1"
            style={{ color: dark ? "#FFB199" : "#B43A23" }}
            data-testid="herald-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className={dark ? "btn-mauve w-full justify-center mt-2" : "btn-primary w-full justify-center mt-2"}
          data-testid="herald-submit"
        >
          {submitting ? "Subscribing…" : "Send Me The HERald"} <ArrowUpRight size={15} />
        </button>
      </form>
    </div>
  );
}
